import { Grid, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ThemePalette } from 'mui.theme';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import CircularProgressChart from './CircularProgress';
import type { FleetDetailsItem } from 'types/utilization.types';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { daysLeftInYear, getShiftHours } from 'views/Utilization/utils';
import { useAppSelector } from 'store/hook';
import { useEffect, useMemo } from 'react';
import TargetSetter from './TargetSetter';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  data: FleetDetailsItem;
  currentDailyAvg: number;
  rollingDailyAverage: number;
  assetType: 'all' | 'loader' | 'hauler';
  setDetailsVal?: any;
}

const Details = ({
  data,
  currentDailyAvg,
  rollingDailyAverage: sevenDayAvg,
  assetType,
  setDetailsVal,
}: Props) => {
  const { t } = useTranslation();
  const isLoading = useAppSelector(
    (state) => state.utilizationReducer.loadingStates.isLoadingFleetDetails
  );
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const { customerCode, unitSystem } = useCustomerInfo();

  const isLoadingFleetPeriodSummary = useAppSelector(
    (state) =>
      state.utilizationReducer.loadingStates.isLoadingFleetPeriodSummary
  );

  const targetData = useAppSelector((state) => state.targetReducer.data.target);
  const isLoadingTarget = useAppSelector(
    (state) => state.targetReducer.loadingStates?.isLoadingTarget
  );

  const noOfAssets = Number(data.number_of_assets);

  const target = useAppSelector(
    (state) => state.targetReducer.data.utilization
  );
  const utilizationTarget = useMemo(
    () => target?.find((item) => item.assetType === assetType),
    [target, assetType]
  );

  const value = useMemo(
    () => Number(data.total_engine_working_minutes) / 60,
    [data]
  ); // in hours

  const targetValue = useMemo(
    () => utilizationTarget?.targetValue,
    [utilizationTarget]
  );

  const ytdTarget = useMemo(
    () => utilizationTarget?.ytdTarget,
    [utilizationTarget]
  );

  const hasMetTarget = useMemo(
    () => value > (targetValue as number),
    [value, targetValue]
  );

  useEffect(() => {
    if (!isLoadingFleetPeriodSummary) {
      setDetailsVal({
        'YTD Target (hours)': formatNumberToShortScale(ytdTarget as number),
        'Annual Productivity Target (hrs)': formatNumberToShortScale(
          (targetValue as number) ||
            365 * getShiftHours(customerCode) * noOfAssets
        ),
        'Rolling Daily Average 7 Days (hrs/day)': truncateTo2dp(sevenDayAvg),
        'Remaining Days': daysLeftInYear(),
        'Current Daily Average 7 Days (hrs/day)': truncateTo2dp(
          currentDailyAvg / 60
        ),
        'Target Met (True/False)': hasMetTarget,
      });
    }
  }, [
    isLoadingFleetPeriodSummary,
    currentDailyAvg,
    sevenDayAvg,
    noOfAssets,
    targetData,
    hasMetTarget,
  ]);

  return (
    <Grid item lg={6} md={6} sm={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.borderGrey}`,
          minHeight: '300px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '30px',
            }}
          >
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '13px',
              }}
            >
              {t('total_productive_hours_ytd')}
            </Typography>
            {assetType !== 'all' && (
              <TargetSetter
                enableDarkTheme={enableDarkTheme}
                assetType={assetType}
              />
            )}
          </Box>
        </Box>
        {/** Content Box */}
        {isLoading ? (
          <MultiColorCircularLoader height="300px" />
        ) : (
          <Box
            sx={{
              paddingTop: '15px',
            }}
          >
            <Grid container>
              <Grid item lg={6} sm={12}>
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <CircularProgressChart
                    enableDarkTheme={enableDarkTheme}
                    value={Number(data.total_engine_working_minutes)}
                    target={(targetValue as number) * 60}
                  />
                  <Box
                    sx={{
                      alignContent: 'center',
                      paddingLeft: '10px',
                    }}
                  >
                    <>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('ytd_target')}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: palette.textPrimary,
                          paddingBottom: '15px',
                        }}
                      >
                        {isLoadingTarget
                          ? 'Loading...'
                          : `${formatNumberToShortScale(
                              ytdTarget as number
                            )} Hours`}
                      </Typography>
                    </>
                    <>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('annual_target')}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: palette.textPrimary,
                        }}
                      >
                        {isLoadingTarget
                          ? 'Loading...'
                          : `${formatNumberToShortScale(
                              targetValue as number
                            )} Hours`}
                      </Typography>
                    </>
                  </Box>
                </Box>
              </Grid>
              <Grid item lg={6} sm={12}>
                <Box>
                  <Grid container>
                    <Grid item lg={6} sm={12}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('annual_productivity_target')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {formatNumberToShortScale(
                          (targetValue as number) ||
                            365 * getShiftHours(customerCode) * noOfAssets
                        )}
                        <span
                          style={{
                            color: palette.textSecondary,
                          }}
                        >
                          {' '}
                          {t('hours')}
                        </span>
                      </Typography>

                      <Typography
                        sx={{
                          paddingTop: '30px',
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('rolling_daily_average_7d')}
                      </Typography>
                      {isLoadingFleetPeriodSummary ? (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {t('cdp_loading')}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {sevenDayAvg > 0 ? (
                            <>
                              {truncateTo2dp(sevenDayAvg)}
                              <span
                                style={{
                                  color: palette.textSecondary,
                                }}
                              >
                                {' '}
                                Hours/Day
                              </span>
                            </>
                          ) : (
                            <>{t('no_data_available')}</>
                          )}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item lg={6} sm={12}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('remaining_days')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {daysLeftInYear()}
                      </Typography>

                      <Typography
                        sx={{
                          paddingTop: '30px',
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('current_daily_average_7d')}
                      </Typography>
                      {isLoadingFleetPeriodSummary ? (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {t('cdp_loading')}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {truncateTo2dp(currentDailyAvg / 60)}
                          <span
                            style={{
                              color: palette.textSecondary,
                            }}
                          >
                            {' '}
                            Hours/Day
                          </span>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {!hasMetTarget ? (
                    <></>
                  ) : (
                    <Box
                      sx={{
                        textAlign: 'center',
                        border: `1px solid ${palette.borderGrey}`,
                        borderRadius: '5px',
                        marginTop: '15px',
                        padding: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ color: '#029161', marginRight: '4px' }}
                        />
                        {t('avg_productive_hours_are_on_target')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box
          sx={{
            marginTop: '50px', // Pushes the box to the bottom
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: enableDarkTheme
                ? '#F3F4F6'
                : ThemePalette.typography.black,
              marginBottom: '4px',
            }}
          >
            {t('averages_shown_above_are_based_on_last_7_days_recent_most_da')}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: enableDarkTheme
                ? '#F3F4F6'
                : ThemePalette.typography.black,
              marginBottom: '4px',
            }}
          >
            {t('ytd_numbers_are_based_on_real_time_data_received_from_assets')}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default Details;
